import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { proforma } from '../services/api';
import { PackagePlus, PackageCheck, RefreshCw, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useToast } from '../context/ToastContext';

interface ReceiveItem {
  inventory_id: number | null;
  article_type: string;
  color: string;
  size: string;
  quantity: number;
  rate: number;
}

interface ReceiveInvoice {
  id: number;
  proforma_id: number;
  proforma_number: string;
  dealer_id: number | null;
  dealer_name: string;
  status: 'pending' | 'stocked';
  total_qty: number;
  items: ReceiveItem[];
  notes: string | null;
  created_at: string;
  stocked_at: string | null;
}

const PurchaseReceives: React.FC = () => {
  const { toast, confirm } = useToast();
  const [receives, setReceives] = useState<ReceiveInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [stockingId, setStockingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    proforma.listReceives()
      .then(r => setReceives(r.data || []))
      .catch(() => toast('Failed to load receive invoices', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAddToStock = async (id: number) => {
    const ok = await confirm('Add all received items to inventory? This cannot be undone.');
    if (!ok) return;
    setStockingId(id);
    try {
      const res = await proforma.addToStock(id);
      toast(res.data.message || 'Added to inventory successfully', 'success');
      load();
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Failed to add to stock', 'error');
    }
    setStockingId(null);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return receives;
    const q = search.toLowerCase();
    return receives.filter(r =>
      r.proforma_number.toLowerCase().includes(q) ||
      r.dealer_name.toLowerCase().includes(q) ||
      r.items.some(i =>
        i.article_type.toLowerCase().includes(q) ||
        i.color.toLowerCase().includes(q) ||
        i.size.toLowerCase().includes(q)
      )
    );
  }, [receives, search]);

  const pending = filtered.filter(r => r.status === 'pending');
  const stocked = filtered.filter(r => r.status === 'stocked');

  const renderTable = (items: ReceiveInvoice[], showStock: boolean) => (
    <div className="table-responsive">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {['Proforma #', 'Dealer', 'Qty', 'Status', 'Date', ''].map(h => (
              <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(r => (
            <React.Fragment key={r.id}>
              <tr style={{ borderBottom: expandedId === r.id ? 'none' : '1px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--accent-blue)', fontWeight: 600 }}>{r.proforma_number}</td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-primary)' }}>{r.dealer_name}</td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>{r.total_qty}</td>
                <td style={{ padding: '12px 16px' }}>
                  {r.status === 'stocked'
                    ? <span style={{ background: 'var(--accent-green-soft)', color: 'var(--accent-green)', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Stocked</span>
                    : <span style={{ background: 'rgba(245,158,11,0.1)', color: 'var(--accent-amber)', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Pending</span>
                  }
                </td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>
                  {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {r.stocked_at && <div style={{ fontSize: 11, color: 'var(--accent-green)', marginTop: 2 }}>Stocked {new Date(r.stocked_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {showStock && r.status === 'pending' && (
                      <button
                        onClick={() => handleAddToStock(r.id)}
                        disabled={stockingId === r.id}
                        style={{ padding: '6px 14px', background: stockingId === r.id ? 'var(--bg-elevated)' : 'linear-gradient(135deg, var(--accent-green), #059669)', border: 'none', borderRadius: 6, color: stockingId === r.id ? 'var(--text-muted)' : '#fff', fontSize: 12, fontWeight: 600, cursor: stockingId === r.id ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                      >
                        <PackageCheck size={13} /> {stockingId === r.id ? 'Adding...' : 'Add to Stock'}
                      </button>
                    )}
                    <button
                      onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                      style={{ padding: '6px 10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)' }}
                    >
                      {expandedId === r.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Items
                    </button>
                  </div>
                </td>
              </tr>
              {expandedId === r.id && (
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <td colSpan={6} style={{ padding: '0 16px 16px 32px', background: 'var(--bg-elevated)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                      <thead>
                        <tr>
                          {['Article', 'Color', 'Size', 'Qty Received', 'Rate (₹)'].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(r.items || []).map((item, i) => (
                          <tr key={i}>
                            <td style={{ padding: '5px 10px', fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{item.article_type}</td>
                            <td style={{ padding: '5px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>{item.color}</td>
                            <td style={{ padding: '5px 10px' }}><span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>{item.size}</span></td>
                            <td style={{ padding: '5px 10px', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{item.quantity}</td>
                            <td style={{ padding: '5px 10px', fontSize: 12, color: 'var(--accent-amber)' }}>₹{item.rate?.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {r.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, fontStyle: 'italic' }}>Notes: {r.notes}</div>}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout title="Purchase Receives" subtitle="Receive stock against proforma invoices and add to inventory">
      <div style={{ maxWidth: 960 }}>
        {/* KPIs */}
        <div className="animate-in" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total Receives', value: receives.length, color: 'var(--accent-blue)' },
            { label: 'Pending Stock', value: receives.filter(r => r.status === 'pending').length, color: 'var(--accent-amber)' },
            { label: 'Stocked', value: receives.filter(r => r.status === 'stocked').length, color: 'var(--accent-green)' },
          ].map(kpi => (
            <div key={kpi.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '18px 22px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 8 }}>{kpi.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
        </div>

        {/* Search + Refresh */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by proforma #, dealer, article..."
              style={{ width: '100%', padding: '9px 12px 9px 34px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <button onClick={load} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)' }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* Pending section */}
        <div className="animate-in-1" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <PackagePlus size={15} color="var(--accent-amber)" />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Pending — Add to Stock</span>
            {pending.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(245,158,11,0.1)', color: 'var(--accent-amber)', fontWeight: 600 }}>{pending.length}</span>}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
          ) : pending.length > 0 ? renderTable(pending, true) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
              <PackagePlus size={32} style={{ marginBottom: 10, opacity: 0.3 }} />
              <div style={{ fontWeight: 500, marginBottom: 4 }}>No pending receives</div>
              <div style={{ fontSize: 12 }}>Go to Proforma → click "Receive" on any invoice to start</div>
            </div>
          )}
        </div>

        {/* Stocked section */}
        {stocked.length > 0 && (
          <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <PackageCheck size={15} color="var(--accent-green)" />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Stocked History</span>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--accent-green-soft)', color: 'var(--accent-green)', fontWeight: 600 }}>{stocked.length}</span>
            </div>
            {renderTable(stocked, false)}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PurchaseReceives;
